import React, {Component} from 'react'
import {Header} from '../header/Header'
import Footer from '../footer/Footer'
import I18nContext from '../i18n/I18nContext'
import {
    doEasyConnect,
    getCaseInfo,
    getEidIdentStatus,
    postUserStart,
    putContactData
} from '../../lib/emmiApi'
import Spinner from '../misc/Spinner'
import UnexpectedError from '../misc/UnexpectedError'
import {
    addCaseIdParam, checkPathInUrl,
    isPathInUrl,
    parseCaseParams,
    removeEasyConnectParams,
    updateQueryParameter
} from 'wipi-common/src/lib/util'
import DataCompletion from '../data-completion/DataCompletion'
import {LOG_EVENTS} from 'wipi-common/src/lib/logging/events'
import CaseGoneError from '../../lib/CaseGoneError'
import CaseGoneInfo from '../misc/CaseGoneInfo'
import CaseInfo from '../case-info/CaseInfo'
import MainContainer from '../misc/MainContainer'
import {
    determineDefaultLanguage,
    loadAppConfig,
    DEFAULT_LANGUAGE,
    getLanguageByCode
} from "wipi-common/src/lib/i18n";
import RateLimitExceededError from '../../lib/RateLimitExceededError'
import RateLimitExceeded from '../misc/RateLimitExceeded'
import WhitelabelStyle from './WhitelabelStyle'
import ErrorBoundary from "../misc/ErrorBoundary";
import LOG from "wipi-common/src/lib/logging/logger";
import CaseConflictInfo from "../misc/CaseConflictInfo";
import CaseConflictError from "../../lib/CaseConflictError";
import CaseCancelledInfo from "../misc/CaseCancelledInfo"
import CaseCancelledError from "../../lib/CaseCancelledError";
import './App.scss';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import CaseUnknownError from "../../lib/CaseUnknownError";
import CaseUnknown from "../misc/CaseUnknown";

class App extends Component {

    state = {
        selectedLanguage: DEFAULT_LANGUAGE,
        selectedTheme: null,
        caseInfo: null,
        caseInfoError: false,
        queryParams: window.location.search,
        hasResourceData: false,
        tcTokenUrlHost: null,
    }

    componentDidMount() {
        localStorage.removeItem("signingWorkflow")
        if(localStorage.getItem("theme") === null && this.state.selectedTheme === null) {
            this.changeTheme("light")
        }
        if(localStorage.getItem("theme") !== this.state.selectedTheme) {
            this.changeTheme(localStorage.getItem("theme"))
        }
        determineDefaultLanguage()
            .then(result => {
                LOG.logInfo(LOG_EVENTS.CE_LANGUAGE_SELECTION, undefined, undefined, result)
                let selectedLanguage = getLanguageByCode(result.selectedLanguage)
                this.setState(() => ({selectedLanguage: selectedLanguage}))
                this.init(selectedLanguage)
            })
    }

    init = language => {
        const code = language.value
        localStorage.setItem("language", code)
        loadAppConfig(code)
            .then((result) => {
                updateQueryParameter("lang", language.value)
                this.setState(() => ({
                    selectedLanguage: language,
                    hasResourceData: true,
                    tcTokenUrlHost: result.tcTokenUrlHost
                }))
                this.updateQueryParam()
            })
            .catch(error => {
                LOG.logError(LOG_EVENTS.REQUEST_ERROR, 'Unable to load app config')
            })
        this.loadCaseInfo()
    }

    updateQueryParam = () => {
        this.setState(() => ({
            queryParams: window.location.search,
        }))
    }

    changeLanguage = language => {
        this.init(language)
    }

    changeTheme = theme => {
        localStorage.setItem("theme", theme);
        this.setState(() => ({selectedTheme: theme}))
        document.documentElement.dataset.bsTheme = theme;
    }


    loadCaseInfo = () => {
        try {
            const caseParams = parseCaseParams()
            if(checkPathInUrl("/eidsuccess")) {
                this.processCaseInfoRequest(getEidIdentStatus(caseParams.caseId))
            } else {
                if (!!caseParams.caseId) {
                    this.processCaseInfoRequest(getCaseInfo(caseParams.caseId))
                } else {
                    this.processCaseInfoRequest(doEasyConnect(caseParams.accountingNumber, caseParams.referenceId))
                        .then(this.updateUrl)
                }
            }
        } catch (error) {
            LOG.logError(LOG_EVENTS.MR_CASE_CHECK_RESULT, 'Could not extract case params')
            this.setState(() => ({caseInfoError: true}))
        }
    }

    getDisplayContent = () => {
        const {caseInfo, caseInfoError, isCaseCancelled, isCaseGone, isCaseUnknown, isConflict,
            isRateLimitExceed, hasResourceData, caseCancelledMessage} = this.state

        if (hasResourceData) {
            if (caseInfoError) {
                LOG.logWarn(LOG_EVENTS.UNEXPECTED_ERROR)
                return <UnexpectedError/>
            } else if(isCaseCancelled){
                return <CaseCancelledInfo message={caseCancelledMessage}/>
            }else if (isCaseGone) {
                return <CaseGoneInfo/>
            } else if (isCaseUnknown) {
                return <CaseUnknown/>
            } else if (isConflict) {
                return <CaseConflictInfo/>
            } else if (isRateLimitExceed) {
                return <RateLimitExceeded/>
            } else if (caseInfo?.contactData) {
                LOG.logInfo(LOG_EVENTS.DC_CONTACT_DATA)
                return <DataCompletion caseInfo={caseInfo} submitData={this.submitContactData}
                                       serverValidationErrors={this.state.serverValidationErrors}/>
            } else if (caseInfo?.modules) {
                return <CaseInfo caseInfo={caseInfo} queryParams={this.state.queryParams} tcTokenUrlHost={this.state.tcTokenUrlHost}/>
            } else {
                return <Spinner />
            }
        } else {
            return <Spinner />
        }
    }

    submitContactData = data => {
        LOG.logInfo(LOG_EVENTS.DC_SEND_CONTACT_DATA)
        return putContactData(this.state.caseInfo.caseId, data)
            .then(caseInfo => this.setState(() => ({caseInfo})))
            .catch(data => {
                const stateUpdate = (data instanceof Error) ? {caseInfoError: true} : {serverValidationErrors: data}
                this.setState(() => stateUpdate)
            })
    }

    processCaseInfoRequest = request => {
        return request
            .then(caseInfo => {
                if (caseInfo.signingWorkFlow && !localStorage.getItem("signingWorkflow")) {
                    localStorage.setItem("signingWorkflow", "true")
                    //Force to change language to default, because the user has no possibility to change the language anymore, tbd!
                    this.changeLanguage(DEFAULT_LANGUAGE)
                } else if (!caseInfo.signingWorkFlow) {
                    localStorage.removeItem("signingWorkflow")
                }
                this.setState(() => ({caseInfo}))
                //skip callback url to call userStart again
                if (!isPathInUrl(['/eIdError', '/eIdSuccess', '/eidsuccess'])){
                    postUserStart(caseInfo.caseId)
                }
                return caseInfo
            })
            .catch(error => {
                let stateUpdate = {}
                if (error instanceof CaseGoneError) {
                    stateUpdate = {isCaseGone: true}
                }else if(error instanceof CaseUnknownError){
                    stateUpdate = {isCaseUnknown: true}
                }else if (error instanceof CaseCancelledError){
                    stateUpdate = {isCaseCancelled : true, caseCancelledMessage: error.message}
                }else if (error instanceof CaseConflictError) {
                    stateUpdate = {isConflict: true}
                } else if (error instanceof RateLimitExceededError) {
                    stateUpdate = {isRateLimitExceed: true}
                } else {
                    stateUpdate = {caseInfoError: true}
                }
                this.setState(() => stateUpdate)
            })
    }

    updateUrl = (caseInfo) => {
        if (caseInfo) {
            let search = removeEasyConnectParams(window.location.search)
            search = addCaseIdParam(search, caseInfo.caseId)
            window.history.replaceState({}, window.document.title, window.location.pathname + search)
            this.updateQueryParam()
        }
    }

    render() {
        const {caseInfo, selectedLanguage, hasResourceData} = this.state

        if (!this.memoizedContextValue) {
            this.memoizedContextValue = { lang: "", ready: false };
        }
        this.memoizedContextValue.lang = selectedLanguage.value;
        this.memoizedContextValue.ready = hasResourceData;

        return <I18nContext.Provider value={this.memoizedContextValue}>
            {caseInfo?.whitelabel && <WhitelabelStyle {...this.state.caseInfo.whitelabel} />}

            <Header changeLanguage={this.changeLanguage}
                    changeTheme={this.changeTheme}
                    signingWorkflow={caseInfo?.signingWorkFlow}
                    selectedLanguage={selectedLanguage}
                    whitelabel={!isCaseLoadedNoWhitelabel(this.state)}
                    principalDisplayName={caseInfo?.principalDisplayName}
                    principalDisplayLogo={caseInfo && (this.state.selectedTheme ==="light" ? caseInfo.principalDisplayLogo : caseInfo.principalDisplayLogoDarkMode)}/>
            <MainContainer><ErrorBoundary>{this.getDisplayContent()}</ErrorBoundary></MainContainer>

            <Footer legalInfo={caseInfo?.legalInfo}/>
        </I18nContext.Provider>
    }
}

function isCaseLoadedNoWhitelabel({caseInfo, caseInfoError, isCaseCancelled, isCaseGone, isCaseUnknown, isConflict, isRateLimitExceed}) {
    return isConflict || isRateLimitExceed || caseInfoError || isCaseCancelled || isCaseGone || isCaseUnknown || (caseInfo && !caseInfo.whitelabel)
}

export default App
